import React from "react";
import "./alertsEvents.css";
import {
  getAllAlertEvents,
  updateAlertEventsBulk,
} from "../../redux/apiCalls/alertsCall/alertEventsCall";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import Pagination from "../../components/Pagination/Pagination";
import { Search, SlidersHorizontal } from "lucide-react";

const PAGE_SIZE = 20;

const AlertsEvents = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.currentUser);
  const navigate = useNavigate();
  const assignedDevices = useSelector(
    (state) => state.devices?.currentDevices || [],
  );

  const assignedDeviceIds = React.useMemo(
    () =>
      Array.isArray(assignedDevices)
        ? Array.from(
            new Set(
              assignedDevices
                .map((d) => Number(d?.id))
                .filter((n) => Number.isFinite(n)),
            ),
          )
        : [],
    [assignedDevices],
  );

  const type = (searchParams.get("type") || "UMBRAL").toUpperCase();
  const deviceName = searchParams.get("device_name") || "";
  const dateFrom = searchParams.get("dateFrom") || "";
  const dateTo = searchParams.get("dateTo") || "";
  const page = Math.max(1, parseInt(searchParams.get("page") || "1", 10));

  const [{ events, total }, setData] = React.useState({ events: [], total: 0 });
  const [isLoading, setIsLoading] = React.useState(false);

  const [deviceInput, setDeviceInput] = React.useState(deviceName);
  const [dateFromInput, setDateFromInput] = React.useState(dateFrom);
  const [dateToInput, setDateToInput] = React.useState(dateTo);

  React.useEffect(() => {
    setDeviceInput(deviceName);
  }, [deviceName]);

  React.useEffect(() => {
    setDateFromInput(dateFrom);
  }, [dateFrom]);

  React.useEffect(() => {
    setDateToInput(dateTo);
  }, [dateTo]);

  const [selectedIds, setSelectedIds] = React.useState(new Set());

  const types = [
    "UMBRAL",
    "MAXIMETRO",
    "COSTE",
    "AUSENCIA",
    "TUNEL",
    "REACTIVA",
  ];

  const formatDateStart = (val) =>
    !val ? "" : new Date(val + "T00:00:00.000Z").toISOString();

  const formatDateEnd = (val) =>
    !val ? "" : new Date(val + "T23:59:59.000Z").toISOString();

  const refreshSeq = React.useRef(0);

  const refreshAlertEvents = React.useCallback(async () => {
    if (!user?.accessToken) return;

    if (!assignedDeviceIds || assignedDeviceIds.length === 0) {
      setData({ events: [], total: 0 });
      return;
    }

    const mySeq = ++refreshSeq.current;
    setIsLoading(true);

    const params = {
      page,
      limit: PAGE_SIZE,
      type,
      device_ids: assignedDeviceIds,
    };

    if (deviceName) params.device_name = deviceName;
    if (dateFrom) params.dateFrom = formatDateStart(dateFrom);
    if (dateTo) params.dateTo = formatDateEnd(dateTo);

    try {
      const { events: evts, total: tot } = await getAllAlertEvents(
        dispatch,
        user.accessToken,
        params,
      );

      if (mySeq !== refreshSeq.current) return;
      setData({ events: evts || [], total: tot || 0 });
    } catch (error) {
      console.error("Error loading alert events:", error);
      if (mySeq === refreshSeq.current) {
        setData({ events: [], total: 0 });
      }
    } finally {
      if (mySeq === refreshSeq.current) setIsLoading(false);
    }
  }, [
    dispatch,
    user?.accessToken,
    page,
    type,
    deviceName,
    dateFrom,
    dateTo,
    assignedDeviceIds,
  ]);

  React.useEffect(() => {
    refreshAlertEvents();
  }, [refreshAlertEvents]);

  const totalEvents = total;
  const localEvents = events;

  const isSelectable = (alert) => !alert?.is_read;
  const isRowSelected = (id) => selectedIds.has(id);

  const toggleRow = (alert) => {
    if (!isSelectable(alert)) return;

    const id = alert.id;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const visibleSelectableIds = localEvents
    .filter(isSelectable)
    .map((a) => a.id);

  const allVisibleSelected =
    visibleSelectableIds.length > 0 &&
    visibleSelectableIds.every((id) => selectedIds.has(id));

  const someVisibleSelected = visibleSelectableIds.some((id) =>
    selectedIds.has(id),
  );

  const toggleSelectAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);

      if (allVisibleSelected) {
        visibleSelectableIds.forEach((id) => next.delete(id));
      } else {
        visibleSelectableIds.forEach((id) => next.add(id));
      }

      return next;
    });
  };

  const setQuery = (updates) => {
    const next = new URLSearchParams(searchParams);

    Object.entries(updates).forEach(([k, v]) => {
      if (v === null || v === undefined || v === "") next.delete(k);
      else next.set(k, String(v));
    });

    setSearchParams(next, { replace: true });
  };

  const setPageQS = (p) => setQuery({ page: Math.max(1, p) });
  const setTypeQS = (t) => setQuery({ type: t.toUpperCase(), page: 1 });
  const setDeviceQS = (v) => setQuery({ device_name: v, page: 1 });
  const setDateFromQS = (v) => setQuery({ dateFrom: v, page: 1 });
  const setDateToQS = (v) => setQuery({ dateTo: v, page: 1 });

  const handleMarkSelectedAsRead = async () => {
    if (!user?.accessToken || selectedIds.size === 0) return;

    const idsToUpdate = Array.from(selectedIds);
    if (idsToUpdate.length === 0) return;

    try {
      await updateAlertEventsBulk(
        dispatch,
        {
          ids: idsToUpdate,
          is_read: true,
        },
        user.accessToken,
      );

      await refreshAlertEvents();

      setSelectedIds((prev) => {
        const next = new Set(prev);
        idsToUpdate.forEach((id) => next.delete(id));
        return next;
      });
    } catch (error) {
      console.error("Error marking selected alert events as read:", error);
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalEvents / PAGE_SIZE));
  const startIdx = (page - 1) * PAGE_SIZE;
  const endIdx = startIdx + (localEvents?.length || 0);

  return (
    <div>
      <div className="cont-path">
        <p className="path path-cursor" onClick={() => navigate(`/`)}>
          <FormattedMessage id="Dashboard" defaultMessage="Escritorio" /> {">"}
        </p>
        <p className="path path-cursor" onClick={() => navigate(`/devices`)}>
          <FormattedMessage id="Devices" defaultMessage="Dispositivos" /> {">"}
        </p>
        <p className="path">
          <FormattedMessage
            id="AlertsEvents"
            defaultMessage="Eventos de alerta"
          />
        </p>
      </div>

      <div className="alertas-principales-container">
        <div
          className="alertas-principales-search-wrapper"
          style={{ justifyContent: "space-between" }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <div className="alertas-principales-filter-wrapper">
              <select
                className="alertas-principales-filter-select"
                value={type}
                onChange={(e) => setTypeQS(e.target.value)}
              >
                {types.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
              <SlidersHorizontal
                size={18}
                className="alertas-principales-filter-icon"
              />
            </div>

            <div className="alertas-principales-search-bar">
              <div className="alertas-principales-search-input-wrapper">
                <input
                  type="search"
                  placeholder="Dispositivo"
                  className="alertas-principales-search-input"
                  value={deviceInput}
                  onChange={(e) => {
                    const v = e.target.value;
                    setDeviceInput(v);
                    if (v === "" && deviceName !== "") {
                      setDeviceQS("");
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      setDeviceQS(deviceInput);
                    }
                  }}
                />
                <Search
                  size={18}
                  className="alertas-principales-search-icon-inside"
                />
              </div>
              <button
                type="button"
                className="alertas-principales-btn alertas-principales-btn--search"
                onClick={() => setDeviceQS(deviceInput)}
                title="Aplicar búsqueda"
              >
                Buscar
              </button>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <label
              className="alertas-principales-label-fecha"
              htmlFor="dateFrom"
            >
              Desde
            </label>
            <input
              type="date"
              id="dateFrom"
              className="alertas-principales-date-input"
              value={dateFromInput}
              onChange={(e) => {
                const v = e.target.value;
                setDateFromInput(v);
                if (v === "" && dateFrom !== "") setDateFromQS("");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  setDateFromQS(dateFromInput);
                }
              }}
            />
            <label className="alertas-principales-label-fecha" htmlFor="dateTo">
              Hasta
            </label>
            <input
              type="date"
              id="dateTo"
              className="alertas-principales-date-input"
              value={dateToInput}
              onChange={(e) => {
                const v = e.target.value;
                setDateToInput(v);
                if (v === "" && dateTo !== "") setDateToQS("");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  setDateToQS(dateToInput);
                }
              }}
            />
            <button
              type="button"
              className="alertas-principales-btn"
              onClick={() => {
                setDateFromQS(dateFromInput);
                setDateToQS(dateToInput);
              }}
              title="Aplicar fechas"
            >
              Aplicar fechas
            </button>
          </div>
        </div>

        <div className={`table-wrapper ${isLoading ? "is-loading" : ""}`}>
          <table className="alertas-principales-table">
            <thead>
              <tr>
                <th style={{ textAlign: "center", width: "50px" }}>
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    ref={(el) => {
                      if (el)
                        el.indeterminate =
                          !allVisibleSelected && someVisibleSelected;
                    }}
                    onChange={toggleSelectAllVisible}
                    title="Seleccionar todos (solo no leídas)"
                  />
                </th>
                <th>Fecha</th>
                <th>Dispositivo</th>
                <th>Mensaje</th>
                <th style={{ minWidth: 180 }}>
                  <button
                    className="alertas-principales-btn"
                    onClick={handleMarkSelectedAsRead}
                    disabled={selectedIds.size === 0 || isLoading}
                    title={
                      selectedIds.size > 0
                        ? `Marcar ${selectedIds.size} como leídas`
                        : "Seleccioná al menos una"
                    }
                  >
                    Marcar como leídas
                  </button>
                </th>
                <th>
                  <Pagination
                    totalItems={totalEvents}
                    startIdx={startIdx}
                    endIdx={endIdx}
                    page={page}
                    setPage={setPageQS}
                    totalPages={totalPages}
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              {localEvents?.map((alert, idx) => {
                const isUnread = !alert?.is_read;
                const selectable = isSelectable(alert);
                const isSelected = isRowSelected(alert.id);

                return (
                  <tr
                    key={alert.id || idx}
                    className={`alert-row ${isUnread ? "is-unread" : ""}`}
                  >
                    <td style={{ textAlign: "center" }}>
                      {selectable ? (
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleRow(alert)}
                          title="Seleccionar"
                        />
                      ) : null}
                    </td>
                    <td>{alert?.triggered_at}</td>
                    <td>
                      {alert?.Device?.name ?? alert?.name ?? alert?.Alert?.name}
                    </td>
                    <td className="alertas-principales-mensaje" colSpan={3}>
                      {alert?.message}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {isLoading && <div className="table-loading-overlay"></div>}
        </div>
      </div>
    </div>
  );
};

export default AlertsEvents;
